// ABOUTME: Layout shared by every step of the license purchase dialog (DVLP-7860).
// ABOUTME: Head and foot hold their place; only the body scrolls, so actions never leave the box.
import { observer } from 'mobx-react';
import { PropsWithChildren, ReactNode } from 'react';
import { Button, Typography } from '@execonline-inc/execonline-ui';
import Currency from '../../../../../Currency';
import { L, T } from '../../../../../Translations';
import { LicenseOffer } from '../../LicenseOffers/Types';

interface PaneProps {
  /** Intercom target for the whole step, so a tour can point at the area. */
  intercomTarget: string;
  head: ReactNode;
  foot?: ReactNode;
  className?: string;
}

export const Pane = observer(function Pane({
  intercomTarget,
  head,
  foot,
  className = '',
  children,
}: PropsWithChildren<PaneProps>) {
  return (
    <div
      data-intercom-target={intercomTarget}
      className={`flex min-h-0 min-w-0 flex-1 flex-col px-6 pb-6 pt-8 md:px-8 ${className}`}
    >
      <div className="flex-none">{head}</div>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      {foot && <div className="flex flex-none flex-wrap justify-end gap-3 pt-6">{foot}</div>}
    </div>
  );
});

interface StepsProps {
  active: 1 | 2;
  /** Only Pay passes this: step 1 becomes a way back. */
  onChoose?: () => void;
}

const dotColors = (on: boolean, done: boolean): string => {
  if (on) {
    return 'border-teal-600 bg-teal-600 text-white';
  }
  if (done) {
    return 'border-teal-600 text-teal-600';
  }
  return 'border-gray-300 text-gray-500';
};

const dot = (n: number, on: boolean, done: boolean) => (
  <span
    className={`grid h-[22px] w-[22px] place-items-center rounded-full border text-xs font-semibold ${dotColors(on, done)}`}
  >
    {n}
  </span>
);

/** Constant across Choose and Pay so the dialog never changes shape between them. */
export const WizardSteps = observer(function WizardSteps({ active, onChoose }: StepsProps) {
  const choose = (
    <span className="inline-flex items-center gap-2">
      {dot(1, active === 1, active > 1)}
      <Typography as="span" variant="bodyMedium" fontWeight={active === 1 ? 'semibold' : 'normal'}>
        <T kind="Choose" />
      </Typography>
    </span>
  );

  return (
    <div className="mb-6 flex items-center gap-3">
      {onChoose ? (
        <Button
          data-intercom-target="license-pay-choose-step-button"
          className="p-0 font-normal"
          color="gray"
          variant="link"
          onClick={onChoose}
        >
          {choose}
        </Button>
      ) : (
        choose
      )}
      <span className="h-px w-14 bg-gray-300" aria-hidden="true" />
      <span className="inline-flex items-center gap-2">
        {dot(2, active === 2, false)}
        <Typography
          as="span"
          variant="bodyMedium"
          fontWeight={active === 2 ? 'semibold' : 'normal'}
        >
          <T kind="Pay" />
        </Typography>
      </span>
    </div>
  );
});

const twoDecimals: Intl.NumberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};

export const Price = observer(function Price({ cents }: { cents: number }) {
  return <Currency priceInDollars={cents / 100} options={twoDecimals} />;
});

// The shelf (banner and picker) drops cents on whole-dollar prices so they scan;
// the Pay step keeps them, where the exact charge matters.
const shelfDecimals = (cents: number): Intl.NumberFormatOptions =>
  cents % 100 === 0 ? { minimumFractionDigits: 0, maximumFractionDigits: 0 } : twoDecimals;

export const ShelfPrice = observer(function ShelfPrice({ cents }: { cents: number }) {
  return <Currency priceInDollars={cents / 100} options={shelfDecimals(cents)} />;
});

/**
 * The term in relative words - "Valid for one year" - rather than a start date,
 * which is always today and only adds reading. Whole months or years read as a
 * length; anything else names the day it ends. Dates come from the server as
 * ISO days at UTC midnight, so they are compared in UTC.
 */
export const TermLabel = observer(function TermLabel({ offer }: { offer: LicenseOffer }) {
  const start = offer.startsOn;
  const end = offer.endsOn;
  const months =
    (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
    (end.getUTCMonth() - start.getUTCMonth());
  const wholeMonths = months > 0 && end.getUTCDate() === start.getUTCDate();

  if (wholeMonths && months === 12) {
    return <T kind="Valid for one year" />;
  }
  if (wholeMonths && months % 12 === 0) {
    return <T kind="Valid for {{count}} years" count={months / 12} />;
  }
  if (wholeMonths && months === 1) {
    return <T kind="Valid for one month" />;
  }
  if (wholeMonths) {
    return <T kind="Valid for {{count}} months" count={months} />;
  }
  return (
    <T kind="Active until <date/>" date={<L localizeable={end} format="short-month-day-year" />} />
  );
});
